import _filterInstanceProperty from "@babel/runtime-corejs3/core-js/instance/filter";
import { defineComponent, computed, ref, watchPostEffect, onMounted } from "@vue/composition-api";
import ReportTabLayout from "../report-tab-layout.vue";
import ReportToolbar from "../report-toolbar.vue";
import MaterialInput from "@Shared/components/inputs/material-input.vue";
import { capitalize, cloneDeep, isEmpty } from "lodash";
import { useI18n } from "vue-i18n-composable";
import SelectLevel from "../select-level.vue";
import SelectApplicationStatus from "../select-application-status.vue";
import SelectFormat from "../select-format.vue";
import DateSection from "@Shared/components/sections/date-section.vue";
import Field from "@Shared/components/sections/field.vue";
import AgentsSelect from "@Broker/@Modules/agents/components/agents-select.vue";
import { useMultipleSelect } from "@Shared/@Composables/generic/useMultipleSelect";
import { useReportsStoreGetters } from "../../store/reports-helpers";
import * as ReportsGetters from "../../store/reports-getters";
import { useVuelidate } from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import { useAccountStoreGetters } from "@Broker/@Modules/main/store/account/account-helpers";
import * as AccountGetters from "@Broker/@Modules/main/store/account/account-getters";
import { REPORT_APPLICATION_STATUS_E, REPORT_CASHFLOW_E, REPORT_DATE_TYPE_E, REPORT_FEE_TYPE_E, REPORT_GROUP_BY_E, REPORT_LEVEL_E, REPORT_SORT_BY_E, REPORT_TYPE_E } from "@Broker/@Types/reports/types";
import { useReportDefaultSettings } from "@Broker/@Composables/reports/useReportDefaultSettings";
import vSelectField from "@Shared/components/form-elements/fields/v-select-field.vue";
import { CLIENT_APPLICATION_STATUS_FOR_REPORT } from "@Shared/js/enum";
import RenewalTrackingContainerLoader from "@Broker/@Modules/clients/reports/reports-containers/renewal-tracking/renewal-tracking-container-loader.vue";
export default defineComponent({
  name: "CommissionsContainer",
  components: {
    ReportTabLayout,
    ReportToolbar,
    MaterialInput,
    SelectLevel,
    SelectApplicationStatus,
    SelectFormat,
    DateSection,
    Field,
    AgentsSelect,
    RenewalTrackingContainerLoader,
    vSelectField
  },
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    runningReport: {
      type: Object,
      default: () => ({
        loading: false,
        loadingSucceeded: true
      })
    }
  },
  setup(props, {
    emit
  }) {
    const {
      t,
      tc
    } = useI18n();
    const {
      handleSelectMultipleChange,
      handleVSelectMultipleChange
    } = useMultipleSelect();
    const unsyncedAppsModal = ref();
    const agentsSelect = ref();
    const feeTypeSelect = ref();
    const deductionTypeSelect = ref();
    const action = ref();
    const {
      GET_COMMISSIONS_STRUCTURE
    } = useReportsStoreGetters([ReportsGetters.GET_COMMISSIONS_STRUCTURE]);
    const {
      CURRENT_ACCOUNT_IS_BROKER,
      CURRENT_USER_USERNAME
    } = useAccountStoreGetters([AccountGetters.CURRENT_ACCOUNT_IS_BROKER, AccountGetters.CURRENT_USER_USERNAME]);
    const commissionsStructure = cloneDeep(GET_COMMISSIONS_STRUCTURE.value);
    const settings = ref(commissionsStructure);
    const resetToDefaultProps = ref({
      disabled: false,
      loading: false,
      loadingSucceeded: true
    });
    const setDefaultProps = ref({
      disabled: false,
      loading: false,
      loadingSucceeded: true
    });
    const options = computed(() => ({
      placeholder: t("global.actions.chooseOption")
    }));
    const brokerOption = computed(() => settings.value.level === REPORT_LEVEL_E.BROKERAGE ? [{
      id: REPORT_LEVEL_E.BROKERAGE,
      text: t("global.doorr.brokerage")
    }] : []);
    const dropdownOptions = computed(() => ({
      dateType: [{
        id: REPORT_DATE_TYPE_E.CLOSING_DATE,
        text: t("global.doorr.closingDate")
      }, {
        text: t("application-reports.firm-statistics.approvalDate"),
        id: REPORT_DATE_TYPE_E.APPROVAL_DATE
      }, {
        text: t("application-reports.acceptedDate"),
        id: REPORT_DATE_TYPE_E.ACCEPTED_DATE
      }, {
        id: REPORT_DATE_TYPE_E.FEE_RECEIVED,
        text: t("application-reports.feeReceived")
      }],
      feeType: [{
        id: "all",
        text: capitalize(tc("global.words.all", 1))
      }, {
        id: REPORT_FEE_TYPE_E.MORTGAGE_INSURANCE,
        text: t("direct-to-lender.commissions.fees.feeTypeOptions.mortgageInsurance")
      }, {
        id: REPORT_FEE_TYPE_E.CLOSING_SERVICES,
        text: t("direct-to-lender.commissions.fees.feeTypeOptions.closingServices")
      }, {
        id: REPORT_FEE_TYPE_E.RETROACTIVE_FEE,
        text: t("direct-to-lender.commissions.fees.feeTypeOptions.retroactiveFee")
      }, {
        id: REPORT_FEE_TYPE_E.ADJUSTMENTS,
        text: t("direct-to-lender.commissions.fees.feeTypeOptions.adjustments")
      }, {
        id: REPORT_FEE_TYPE_E.REVERSE_FINDER_FEE,
        text: t("direct-to-lender.commissions.fees.feeTypeOptions.reverseFinderFee")
      }, {
        id: REPORT_FEE_TYPE_E.REFERRAL_FEE,
        text: t("direct-to-lender.commissions.fees.feeTypeOptions.referralFee")
      }, {
        id: REPORT_FEE_TYPE_E.BONUS,
        text: t("direct-to-lender.commissions.fees.feeTypeOptions.bonus")
      }, {
        id: REPORT_FEE_TYPE_E.OTHER,
        text: t("direct-to-lender.commissions.fees.feeTypeOptions.other")
      }],
      deductionType: [{
        id: "all",
        text: capitalize(tc("global.words.all", 1))
      }, {
        text: tc("direct-to-lender.commissions.deductions.deductionTypes.officeFees"),
        id: "office-fees"
      }, {
        text: tc("direct-to-lender.commissions.deductions.deductionTypes.creditBureaus"),
        id: "credit-bureaus"
      }, {
        text: tc("direct-to-lender.commissions.deductions.deductionTypes.affiliation"),
        id: "affiliation"
      }, {
        text: tc("direct-to-lender.commissions.deductions.deductionTypes.appraisal"),
        id: "appraisal"
      }, {
        text: tc("direct-to-lender.commissions.deductions.deductionTypes.clientRebate"),
        id: "client-rebate"
      }, {
        text: tc("direct-to-lender.commissions.deductions.deductionTypes.charity"),
        id: "charity"
      }, {
        text: tc("direct-to-lender.commissions.deductions.deductionTypes.royaltyFees"),
        id: "royalty-fees"
      }, {
        text: tc("direct-to-lender.commissions.deductions.deductionTypes.other"),
        id: "other"
      }],
      cashflow: [{
        id: REPORT_CASHFLOW_E.PAID,
        text: t("application-reports.commissions.paid")
      }, {
        id: REPORT_CASHFLOW_E.RECEIVABLE,
        text: t("application-reports.commissions.receivable")
      }],
      groupBy: [{
        id: REPORT_GROUP_BY_E.APPLICATION_STATUS,
        text: t("application-reports.applicationStatus")
      }, {
        id: REPORT_GROUP_BY_E.LENDER,
        text: t("global.doorr.lender")
      }, {
        id: REPORT_GROUP_BY_E.MORTGAGE_TYPE,
        text: t("application-reports.mortgageType")
      }, {
        id: REPORT_GROUP_BY_E.AGENT,
        text: capitalize(tc("global.doorr.agent", 1))
      }, ...brokerOption.value],
      sortBy: [{
        id: REPORT_SORT_BY_E.DEAL_STATUS,
        text: t("application-reports.applicationStatus")
      }, {
        id: REPORT_SORT_BY_E.LENDER,
        text: t("global.doorr.lender")
      }, {
        id: REPORT_SORT_BY_E.MORTGAGE_TYPE,
        text: t("application-reports.mortgageType")
      }, {
        id: REPORT_SORT_BY_E.APPLICATION_ID,
        text: t("application.applicationID")
      }, {
        id: REPORT_SORT_BY_E.APPLICATION_TYPE,
        text: t("application-reports.applicationType")
      }, {
        id: REPORT_SORT_BY_E.AGENT,
        text: capitalize(tc("global.doorr.agent", 1))
      }, ...brokerOption.value]
    }));
    const showAgentSelect = computed(() => CURRENT_ACCOUNT_IS_BROKER.value && settings.value.level === REPORT_LEVEL_E.AGENT);
    const rules = computed(() => ({
      settings: {
        level: {
          required
        },
        format: {
          required
        },
        applicationStatus: {
          required
        },
        feeType: {
          required
        },
        deductionType: {
          required
        },
        cashflow: {
          required
        },
        dateType: {
          required
        },
        dateFrom: {
          required
        },
        dateTo: {
          required
        },
        groupBy: {
          required
        },
        agents: showAgentSelect.value ? {
          required
        } : {}
      }
    }));
    const v$ = useVuelidate(rules, {
      settings
    });
    onMounted(() => {
      setDefaultAgentIfNeeded();
    });
    const handleChangeDates = range => {
      settings.value.dateFrom = range.start;
      settings.value.dateTo = range.end;
    };
    const handleSelectAgentChange = (value, type) => {
      settings.value.agents = handleVSelectMultipleChange(value, type, settings.value.agents, true);
    };
    const handleChangeFeeType = (selectedValue, type) => {
      var _feeTypeSelect$value;
      if (selectedValue.id === "all" && type === "select") (_feeTypeSelect$value = feeTypeSelect.value) == null ? void 0 : _feeTypeSelect$value.close();
      action.value = "";
      settings.value.feeType = handleSelectMultipleChange(selectedValue, type, settings.value.feeType, true);
    };
    const handleChangeDeductionType = (selectedValue, type) => {
      var _deductionTypeSelect$;
      if (selectedValue.id === "all" && type === "select") (_deductionTypeSelect$ = deductionTypeSelect.value) == null ? void 0 : _deductionTypeSelect$.close();
      action.value = "";
      settings.value.deductionType = handleSelectMultipleChange(selectedValue, type, settings.value.deductionType, true);
    };
    const handleChangeCashflow = selectedValue => {
      settings.value.cashflow = selectedValue.id;
      if (selectedValue.id === REPORT_CASHFLOW_E.PAID) {
        settings.value.dateType = REPORT_DATE_TYPE_E.CLOSING_DATE;
      }
      action.value = "";
    };
    const filteredDateType = computed(() => {
      var _context;
      return _filterInstanceProperty(_context = dropdownOptions.value.dateType).call(_context, dti => settings.value.cashflow === "paid" || dti.id !== "fee-received");
    });
    const primaryOptions = computed(() => {
      var _context2;
      return _filterInstanceProperty(_context2 = dropdownOptions.value.sortBy).call(_context2, val => val.id !== settings.value.sortBySecondary);
    });
    const secondaryOptions = computed(() => {
      var _context3;
      return _filterInstanceProperty(_context3 = dropdownOptions.value.sortBy).call(_context3, val => val.id !== settings.value.sortByPrimary);
    });
    const handleClearSelection = () => {
      var _feeTypeSelect$value2, _deductionTypeSelect$2;
      settings.value = cloneDeep(commissionsStructure);
      settings.value.level = REPORT_LEVEL_E.EMPTY;
      settings.value.agents = "all";
      settings.value.feeType = "";
      settings.value.deductionType = "";
      settings.value.cashflow = REPORT_CASHFLOW_E.PAID;
      settings.value.applicationStatus = REPORT_APPLICATION_STATUS_E.EMPTY;
      settings.value.format = "";
      settings.value.dateType = REPORT_DATE_TYPE_E.EMPTY;
      settings.value.dateFrom = "";
      settings.value.dateTo = "";
      settings.value.groupBy = REPORT_GROUP_BY_E.EMPTY;
      settings.value.sortByPrimary = REPORT_SORT_BY_E.EMPTY;
      settings.value.sortBySecondary = REPORT_SORT_BY_E.EMPTY;
      setDefaultAgentIfNeeded();
      (_feeTypeSelect$value2 = feeTypeSelect.value) == null ? void 0 : _feeTypeSelect$value2.clearSelection();
      (_deductionTypeSelect$2 = deductionTypeSelect.value) == null ? void 0 : _deductionTypeSelect$2.clearSelection();
      action.value = "clear";
    };
    const {
      loadReportDefaultValues,
      updateReportDefaults
    } = useReportDefaultSettings({
      reportType: "commissions",
      reportDataRef: settings
    });
    const handleResetToDefault = () => {
      loadReportDefaultValues();
      setDefaultAgentIfNeeded();
      action.value = "reset";
    };
    const handleSetDefault = async () => {
      setDefaultProps.value.loading = true;
      await updateReportDefaults(settings.value);
      setDefaultProps.value.loading = false;
    };
    const handleRunReport = async () => {
      emit("run-report", {
        reportType: REPORT_TYPE_E.COMMISSIONS,
        data: cloneDeep(settings.value)
      });
    };
    const setDefaultAgentIfNeeded = () => {
      if (!CURRENT_ACCOUNT_IS_BROKER.value) settings.value.agents = [CURRENT_USER_USERNAME.value];
    };
    watchPostEffect(() => {
      if (!settings.value) {
        settings.value = cloneDeep(commissionsStructure);
        return;
      }
      if (isEmpty(settings.value.cashflow)) {
        settings.value.cashflow = REPORT_CASHFLOW_E.PAID;
      }
      if (isEmpty(settings.value.agents)) {
        settings.value.agents = "all";
      }
    });
    const statusToExclude = ref(["all", CLIENT_APPLICATION_STATUS_FOR_REPORT.submitted, CLIENT_APPLICATION_STATUS_FOR_REPORT.declined, CLIENT_APPLICATION_STATUS_FOR_REPORT.pending, CLIENT_APPLICATION_STATUS_FOR_REPORT.approved, CLIENT_APPLICATION_STATUS_FOR_REPORT.accepted, CLIENT_APPLICATION_STATUS_FOR_REPORT.cancelled]);
    return {
      settings,
      resetToDefaultProps,
      setDefaultProps,
      options,
      v$,
      agentsSelect,
      statusToExclude,
      unsyncedAppsModal,
      action,
      dropdownOptions,
      showAgentSelect,
      filteredDateType,
      primaryOptions,
      secondaryOptions,
      handleSelectMultipleChange,
      handleVSelectMultipleChange,
      handleChangeDates,
      handleSelectAgentChange,
      handleChangeFeeType,
      handleChangeDeductionType,
      handleChangeCashflow,
      handleClearSelection,
      handleResetToDefault,
      handleSetDefault,
      handleRunReport,
      setDefaultAgentIfNeeded
    };
  }
});